import { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const CookieModal = ({ onClose, onAcceptAll, onSave }) => {
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState(false);
  const [preferences, setPreferences] = useState(false);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    const modal = document.getElementById('cookie-modal');
    if (modal) modal.focus();
  }, []);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="cookie-modal-backdrop">
      <div className="absolute inset-0 bg-black/40" />
      <div
        id="cookie-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('cookie.modal_title')}
        tabIndex={-1}
        className="relative bg-white w-full max-w-lg mx-4 shadow-lg outline-none"
        data-testid="cookie-modal"
        style={{fontFamily:'IBM Plex Sans,sans-serif'}}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900" style={{fontFamily:'Outfit,sans-serif'}}>{t('cookie.modal_title')}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="cookie-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">{t('cookie.essential')}</p>
              <p className="text-xs text-gray-500">{t('cookie.essential_desc')}</p>
            </div>
            <div className="w-10 h-5 bg-green-600 rounded-full relative cursor-not-allowed opacity-70">
              <div className="absolute right-0.5 top-0.5 w-4 h-4 bg-white rounded-full" />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">{t('cookie.analytics')}</p>
              <p className="text-xs text-gray-500">{t('cookie.analytics_desc')}</p>
            </div>
            <button
              onClick={() => setAnalytics(!analytics)}
              className={`w-10 h-5 rounded-full relative transition-colors ${analytics ? 'bg-green-600' : 'bg-gray-300'}`}
              data-testid="cookie-toggle-analytics"
            >
              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${analytics ? 'right-0.5' : 'left-0.5'}`} />
            </button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">{t('cookie.preferences')}</p>
              <p className="text-xs text-gray-500">{t('cookie.preferences_desc')}</p>
            </div>
            <button
              onClick={() => setPreferences(!preferences)}
              className={`w-10 h-5 rounded-full relative transition-colors ${preferences ? 'bg-green-600' : 'bg-gray-300'}`}
              data-testid="cookie-toggle-preferences"
            >
              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${preferences ? 'right-0.5' : 'left-0.5'}`} />
            </button>
          </div>
        </div>
        <div className="flex gap-3 p-5 border-t border-gray-200">
          <button
            onClick={() => onSave({ analytics, preferences })}
            className="flex-1 py-2.5 text-sm font-medium bg-green-700 text-white hover:bg-green-800 transition-colors"
            data-testid="cookie-save-preferences"
          >
            {t('cookie.save')}
          </button>
          <button
            onClick={onAcceptAll}
            className="flex-1 py-2.5 text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
            data-testid="cookie-accept-all-modal"
          >
            {t('cookie.accept_all')}
          </button>
        </div>
      </div>
    </div>
  );
};

export const CookieBanner = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!window._cookieConsent) {
      setVisible(true);
    }
  }, []);

  const openModal = useCallback(() => {
    setShowModal(true);
  }, []);

  useEffect(() => {
    window.openCookieModal = openModal;
    return () => { delete window.openCookieModal; };
  }, [openModal]);

  const acceptAll = () => {
    window._cookieConsent = 'all';
    setVisible(false);
    setShowModal(false);
  };

  const dismissEssential = () => {
    window._cookieConsent = 'essential';
    setVisible(false);
  };

  const savePreferences = (prefs) => {
    window._cookieConsent = prefs.analytics || prefs.preferences ? 'custom' : 'essential';
    window._cookiePrefs = prefs;
    setVisible(false);
    setShowModal(false);
  };

  return (
    <>
      {visible && (
        <div
          className="fixed bottom-0 left-0 right-0 z-[90] bg-gray-50 border-t border-gray-200"
          data-testid="cookie-banner"
          style={{fontFamily:'IBM Plex Sans,sans-serif'}}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm text-gray-700">
                  {t('cookie.banner_1')}
                </p>
                <p className="text-sm text-gray-500">
                  {t('cookie.banner_2')}
                </p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <button
                  onClick={acceptAll}
                  className="px-5 py-2 text-sm font-medium bg-green-700 text-white hover:bg-green-800 transition-colors"
                  data-testid="cookie-accept-all"
                >
                  {t('cookie.accept_all')}
                </button>
                <button
                  onClick={() => setShowModal(true)}
                  className="px-5 py-2 text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
                  data-testid="cookie-manage-preferences"
                >
                  {t('cookie.manage')}
                </button>
                <button
                  onClick={dismissEssential}
                  className="p-1.5 text-gray-400 hover:text-gray-600"
                  aria-label="Dismiss"
                  data-testid="cookie-dismiss"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <CookieModal
          onClose={() => setShowModal(false)}
          onAcceptAll={acceptAll}
          onSave={savePreferences}
        />
      )}
    </>
  );
};
